// frontend/src/components/AddStockAdjustments.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Package, Plus, Building, Warehouse, ArrowUp, ArrowDown, Calendar, FileText,
  Loader2, AlertCircle, DollarSign
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./StockAdjustments.css";

const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper to extract currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const AddStockAdjustments = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency: contextFormatCurrency } = useCurrency();
  const navigate = useNavigate();

  // ✅ Get company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  // ✅ Currency formatter
  const formatCurrency = useCallback((amount) => {
    const currencyCode = companyCurrency;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currencyCode} ${Number(amount || 0).toFixed(2)}`;
    }
  }, [companyCurrency]);

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    warehouse_id: "",
    product_id: "",
    quantity: "",
    adjustment_type: "in",
    reason: "",
    adjustment_date: new Date().toISOString().split('T')[0],
    notes: "",
  });

  // Dropdown data
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productStock, setProductStock] = useState({});

  // Loading states
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [focusedFields, setFocusedFields] = useState({});

  // Check permissions – allow admin, system admin, AND sales
  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  const isCompanyAdmin = userType === 'company' && currentUser?.role === 'admin';
  const isCompanySales = userType === 'company' && (currentUser?.role === 'admin' || currentUser?.role === 'sales' || currentUser?.role === 'purchase' || currentUser?.role === 'user');
  const canCreate = isCompanyAdmin || isSystemAdmin || isCompanySales;

  // Get company ID
  const getCompanyId = () => {
    if (userType === 'company' && company) {
      return company._id || company;
    }
    return null;
  };

  // Fetch products for specific warehouse with stock info
  const fetchProductsForWarehouse = async (warehouseId) => {
    if (!warehouseId) {
      setFilteredProducts(products);
      setProductStock({});
      return;
    }

    try {
      setLoadingProducts(true);
      const companyId = getCompanyId();

      const response = await axios.get(`${url}/api/inventorystock/warehouse/${warehouseId}`, {
        params: {
          company_id: companyId,
          status: 'active'
        }
      });

      if (response.data.success) {
        const stockMap = {};
        const warehouseProducts = (response.data.data || []).map(stock => {
          const product = stock.product_id || {};
          stockMap[product._id] = {
            current_stock: stock.quantity || 0,
            available_stock: stock.available_qty || 0,
            avg_cost: stock.avg_cost || 0
          };
          return {
            _id: product._id,
            product_id: product._id,
            product_code: product.product_code || 'N/A',
            product_name: product.product_name || 'Unknown',
            current_stock: stock.quantity || 0,
            available_stock: stock.available_qty || 0,
            avg_cost: stock.avg_cost || 0
          };
        });

        setProductStock(stockMap);
        setFilteredProducts(warehouseProducts);
      } else {
        setFilteredProducts(products);
        setProductStock({});
      }
    } catch (error) {
      setFilteredProducts(products);
      setProductStock({});
    } finally {
      setLoadingProducts(false);
    }
  };

  // Handle warehouse change
  const handleWarehouseChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (value) {
      fetchProductsForWarehouse(value);
    } else {
      setFilteredProducts(products);
      setProductStock({});
    }
  };

  // Fetch all dropdown data
  useEffect(() => {
    const fetchData = async () => {
      if (!canCreate) {
        toast.error("You don't have permission");
        navigate("/");
        return;
      }

      try {
        setLoading(true);

        const companyId = getCompanyId();

        if (!companyId) {
          setError("No company ID found");
          setLoading(false);
          return;
        }

        const params = { company_id: companyId, status: 'active' };

        // Fetch warehouses
        try {
          const whRes = await axios.get(`${url}/api/warehouse/dropdown`, { params });
          if (whRes.data?.success) {
            setWarehouses(whRes.data.data || []);
          }
        } catch (err) {
          setWarehouses([]);
        }

        // Fetch all products first
        try {
          const prodRes = await axios.get(`${url}/api/products/dropdown`, { params });
          if (prodRes.data?.success) {
            setProducts(prodRes.data.data || []);
            setFilteredProducts(prodRes.data.data || []);
          }
        } catch (err) {
          setProducts([]);
          setFilteredProducts([]);
        }

      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handlers
  const handleFocus = (field) =>
    setFocusedFields((prev) => ({ ...prev, [field]: true }));

  const handleBlur = (field) => {
    const value = formData[field];
    setFocusedFields((prev) => ({
      ...prev,
      [field]: (typeof value === 'string' ? value.trim() : value) ? true : false
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'warehouse_id') {
      handleWarehouseChange(e);
    } else if (name === 'quantity') {
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = () => {
    if (!formData.warehouse_id) {
      toast.error("Warehouse is required");
      return false;
    }
    if (!formData.product_id) {
      toast.error("Product is required");
      return false;
    }
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      toast.error("Quantity must be greater than 0");
      return false;
    }
    if (!formData.reason) {
      toast.error("Reason is required");
      return false;
    }

    // For out adjustments, check stock availability
    if (formData.adjustment_type === 'out') {
      const qty = parseFloat(formData.quantity);
      const stockInfo = productStock[formData.product_id];
      if (stockInfo && stockInfo.current_stock < qty) {
        toast.error(`Insufficient stock. Available: ${stockInfo.current_stock}, Requested out: ${qty}`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const companyId = getCompanyId();

      // Determine signed quantity based on adjustment type
      const qty = parseFloat(formData.quantity);
      const signedQuantity = formData.adjustment_type === 'in' ? qty : -qty;

      const payload = {
        company_id: companyId,
        warehouse_id: formData.warehouse_id,
        product_id: formData.product_id,
        quantity: signedQuantity,
        adjustment_type: formData.adjustment_type,
        reason: formData.reason,
        adjustment_date: formData.adjustment_date,
        notes: formData.notes || "",
      };

      const res = await axios.post(`${url}/api/stockadjustments`, payload, {
        headers: {
          "Content-Type": "application/json"
        },
      });

      if (res.data.success) {
        toast.success("Stock adjustment request created successfully!");
        setTimeout(() => {
          navigate("/ViewStockAdjustments");
        }, 1500);
      }
    } catch (error) {
      let errorMsg = "Failed to create stock adjustment";

      if (error.response) {
        errorMsg = error.response.data?.message || errorMsg;
        if (error.response.data?.errors) {
          errorMsg = Object.values(error.response.data.errors).join(", ");
        }
      }

      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/ViewStockAdjustments");
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="branch-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading stock adjustment form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-wrapper">
        <div className="branch-form-container">
          <div className="error-container">
            <AlertCircle size={48} color="#dc3545" />
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button onClick={handleCancel} className="cancel-btn">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="branch-form-container">
        <h2 className="form-title">
          <Package size={24} /> New Stock Adjustment
        </h2>

        {/* Company Info Banner */}
        {company && (
          <div className="company-banner">
            <Building size={16} />
            <span>Creating stock adjustment for: <strong>{company.company_name}</strong> ({company.company_code})</span>
            <span style={{ marginLeft: 'auto', fontSize: '0.8rem' }}>
              Currency: {companyCurrency}
            </span>
          </div>
        )}

        <div className="header">
          <h2></h2>
          <button onClick={() => navigate("/ViewStockAdjustments")} className="add-button">
            <Plus size={16} /> View Adjustments
          </button>
        </div>

        <form className="branch-form" onSubmit={handleSubmit}>
          {/* Warehouse Selection */}
          <div className="form-field">
            <select
              name="warehouse_id"
              value={formData.warehouse_id}
              onChange={handleChange}
              required
              disabled={warehouses.length === 0}
            >
              <option value="">
                {warehouses.length === 0 ? "No warehouses available" : "Select Warehouse *"}
              </option>
              {warehouses.map(warehouse => (
                <option key={warehouse._id} value={warehouse._id}>
                  {warehouse.warehouse_name} ({warehouse.warehouse_code})
                </option>
              ))}
            </select>
            <label>Warehouse *</label>
          </div>

          {/* Product Selection */}
          <div className="form-field">
            <select
              name="product_id"
              value={formData.product_id}
              onChange={handleChange}
              required
              disabled={!formData.warehouse_id || loadingProducts}
            >
              <option value="">
                {!formData.warehouse_id ? "Select warehouse first" :
                 loadingProducts ? "Loading products..." :
                 filteredProducts.length === 0 ? "No products available" : "Select Product *"}
              </option>
              {filteredProducts.map(product => (
                <option key={product._id} value={product._id}>
                  {product.product_name} ({product.product_code})
                  {productStock[product._id] && ` - Stock: ${productStock[product._id].current_stock}`}
                  {productStock[product._id] && productStock[product._id].avg_cost > 0 && 
                    ` @ ${formatCurrency(productStock[product._id].avg_cost)}`}
                </option>
              ))}
            </select>
            <label>Product *</label>
            {formData.product_id && productStock[formData.product_id] && (
              <small style={{ display: 'block', marginTop: 4, color: '#666' }}>
                Current Stock: {productStock[formData.product_id].current_stock} units
                {productStock[formData.product_id].avg_cost > 0 && 
                  ` | Avg Cost: ${formatCurrency(productStock[formData.product_id].avg_cost)}`}
              </small>
            )}
          </div>

          {/* Adjustment Type */}
          <div className="form-field">
            <select
              name="adjustment_type"
              value={formData.adjustment_type}
              onChange={handleChange}
              required
            >
              <option value="in">Stock In (+ Add to inventory)</option>
              <option value="out">Stock Out (- Remove from inventory)</option>
            </select>
            <label>Adjustment Type *</label>
          </div>

          {/* Quantity */}
          <div className={`form-field ${focusedFields.quantity || formData.quantity ? "filled" : ""}`}>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              onFocus={() => handleFocus("quantity")}
              onBlur={() => handleBlur("quantity")}
              placeholder="Adjustment quantity"
              min="0.01"
              step="0.01"
              required
            />
            <label>Quantity *</label>
          </div>

          {/* Adjustment Date */}
          <div className="form-field filled">
            <input
              type="date"
              name="adjustment_date"
              value={formData.adjustment_date}
              onChange={handleChange}
              required
            />
            <label>
              <Calendar size={14} className="inline-icon" /> Adjustment Date
            </label>
          </div>

          {/* Reason */}
          <div className={`form-field ${focusedFields.reason || formData.reason ? "filled" : ""}`}>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              onFocus={() => handleFocus("reason")}
              onBlur={() => handleBlur("reason")}
              placeholder="Enter reason for adjustment..."
              rows="3"
              required
            />
            <label>
              <FileText size={14} className="inline-icon" /> Reason *
            </label>
          </div>

          {/* Notes (Optional) */}
          <div className={`form-field ${focusedFields.notes || formData.notes ? "filled" : ""}`}>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              onFocus={() => handleFocus("notes")}
              onBlur={() => handleBlur("notes")}
              placeholder="Additional notes (optional)..."
              rows="2"
            />
            <label>
              <FileText size={14} className="inline-icon" /> Notes (Optional)
            </label>
          </div>

          {/* Adjustment Preview */}
          <div className="adjustment-preview">
            <div className="preview-header">
              <Package size={16} />
              <span>Adjustment Preview</span>
            </div>
            <div className="preview-details">
              <div className="preview-item">
                <span>Type:</span>
                <span className={`preview-value ${formData.adjustment_type === 'in' ? 'in' : 'out'}`}>
                  {formData.adjustment_type === 'in' ? (
                    <>
                      <ArrowUp size={12} /> Stock In (+{formData.quantity || 0})
                    </>
                  ) : (
                    <>
                      <ArrowDown size={12} /> Stock Out (-{formData.quantity || 0})
                    </>
                  )}
                </span>
              </div>
              {formData.product_id && productStock[formData.product_id] && (
                <div className="preview-item">
                  <span>Avg Cost:</span>
                  <span className="preview-value">
                    {formatCurrency(productStock[formData.product_id].avg_cost)}
                  </span>
                </div>
              )}
              <div className="preview-item">
                <span>Status:</span>
                <span className="preview-value pending">Pending Approval</span>
              </div>
            </div>
          </div>

          <div className="form-info">
            <small>
              <Package size={12} /> This adjustment request requires approval before affecting actual inventory.
            </small>
          </div>

          <div className="button-group">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="submit-btn"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Creating...
                </>
              ) : (
                "Create Adjustment Request"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddStockAdjustments;